/**
 * Admin Shell (F100.1)
 *
 * The /azzmin-access surface: one gate, three tabs in a left nav.
 * - Site-Analytics   -> AdminAnalyticsTab (dashboard, link builder, backup)
 * - Site-Telemetry   -> coming soon (future logging UI)
 * - Resume-Generator -> the existing AdminResumePage, unchanged, rendered inShell
 *
 * Mounting the shell also sets the own-traffic opt-out flag so the admin's
 * visits never pollute the analytics.
 */

import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { setNoTrack } from '../utils/track';
import AdminAnalyticsTab from './AdminAnalyticsTab';
import AdminResumePage from './AdminResumePage';

const TABS = [
  { id: 'analytics', label: 'Site-Analytics' },
  { id: 'telemetry', label: 'Site-Telemetry' },
  { id: 'resume', label: 'Resume-Generator' },
];

const TelemetryTab = () => (
  <div className="bg-neutral-800 border border-neutral-700 rounded-xl p-10 text-center">
    <h2 className="text-lg font-semibold text-neutral-200">Site-Telemetry</h2>
    <p className="mt-2 text-sm text-neutral-400">
      Coming soon: the logging UI, built on the same dashboard components with telemetry data.
    </p>
  </div>
);

const AdminShell = () => {
  const { logout, isLoading: authLoading } = useAdminAuth({ requireAuth: true });
  const [tab, setTab] = useState('analytics');

  // Own-traffic exclusion: anyone who reaches the admin shell is the admin.
  useEffect(() => {
    setNoTrack();
  }, []);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-neutral-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-neutral-600 border-t-sky-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-900">
      {/* Header */}
      <header className="bg-neutral-800 border-b border-neutral-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <h1 className="text-xl font-bold text-neutral-100">Admin</h1>
          <button
            onClick={logout}
            className="px-4 py-2 text-sm text-neutral-400 hover:text-neutral-200 hover:bg-neutral-700 rounded-lg transition-colors"
          >
            Logout
          </button>
        </div>
      </header>

      {/* Body: left nav + content */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col md:flex-row gap-6">
        <nav className="md:w-48 shrink-0">
          {/* Mobile: native dropdown (nothing to overflow); desktop: vertical list */}
          <select
            value={tab}
            onChange={(e) => setTab(e.target.value)}
            className="md:hidden w-full bg-neutral-800 border border-neutral-700 rounded-lg px-3 py-2.5 text-sm text-neutral-200 outline-none"
            aria-label="Admin section"
          >
            {TABS.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <ul className="hidden md:flex md:flex-col gap-1">
            {TABS.map((t) => (
              <li key={t.id}>
                <button
                  onClick={() => setTab(t.id)}
                  className={`w-full text-left px-4 py-2.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                    tab === t.id
                      ? 'bg-sky-600/20 text-sky-300 border border-sky-700/50'
                      : 'text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 border border-transparent'
                  }`}
                >
                  {t.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        <main className="flex-1 min-w-0">
          {tab === 'analytics' && <AdminAnalyticsTab />}
          {tab === 'telemetry' && <TelemetryTab />}
          {tab === 'resume' && <AdminResumePage inShell />}
        </main>
      </div>

      <footer className="py-6">
        <p className="text-center text-xs text-neutral-600">Private Admin Interface</p>
      </footer>
    </div>
  );
};

export default AdminShell;
